import React, { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigationType, useParams, useSearchParams } from 'react-router-dom';
import { DetailPageFrame, DetailSection } from '@/components/football';
import Button from '@/components/ui/Button';
import DelayedLoading from '@/components/ui/DelayedLoading';
import EmptyState from '@/components/ui/EmptyState';
import LoadingStatus from '@/components/ui/LoadingStatus';
import AIChatPanel from '@/components/wyscout/AIChatPanel';
import MatchAdvancedStats from '@/components/wyscout/MatchAdvancedStats';
import MatchBuildUpPlays from '@/components/wyscout/MatchBuildUpPlays';
import MatchPlayerStats from '@/components/wyscout/MatchPlayerStats';
import { useLeagueRuntime } from '@/context/LeagueRuntimeContext';
import SectionQuery from '@/pages/matches/detail/SectionQuery';
import WyscoutEventsAnalysis from '@/pages/matches/detail/WyscoutEventsAnalysis';
import WyscoutLineups from '@/pages/matches/detail/WyscoutLineups';
import WyscoutMatchOverview from '@/pages/matches/detail/WyscoutMatchOverview';
import { MatchPageSection, MatchSectionNavigation, scrollToMatchSection } from '@/pages/matches/detail/MatchSectionNavigation';
import type { GoalFocus } from '@/pages/matches/detail/wyscout-events/types';
import { matchesV2API, type AIChatMessageRecord, type AIChatSession } from '@/services/api';
import { Capability, getV2Error, hasCapability } from '@/services/api/v2';
import { leagueAPIv2 } from './api';
import { positiveRouteId, safeExternalPlayerReturnTo, safeLeagueReturnTo } from './detailNavigation';
import { parseLeagueMatchDetailUrl, serializeLeagueMatchDetailUrl } from './matchDetailUrlState';
import { matchDate, matchName, matchScore, StatusLabel } from './presentation';
import { leagueQueryKeys } from './queryKeys';
import type { LeagueAnalysisPreset, LeagueAnalysisSession, LeagueAnalysisSessionsResponse, LeagueBuildUpSection, LeagueEventsSection, LeagueLineupSection, LeagueMatchResponse, LeagueMatchSummary, LeagueMatchSynchronizationResponse, LeagueMatchTab, LeaguePlayerStatsSection, LeagueSectionResponse, LeagueTeamStatsSection } from './types';

const dataSections = [
  { id: 'overview', label: 'Overview' },
  { id: 'lineup', label: 'Lineup' },
  { id: 'events', label: 'Events' },
  { id: 'team-stats', label: 'Team stats' },
  { id: 'player-stats', label: 'Player stats' },
  { id: 'build-up', label: 'Build-up' },
] as const;
const analysisItem = { id: 'analysis', label: 'Analysis' } as const;
type DataSection = typeof dataSections[number]['id'];

const sectionEmptyMessages = {
  lineup: 'No lineup is available for this match.',
  events: 'No events are available for this match.',
  'team-stats': 'No team statistics are available for this match.',
  'player-stats': 'No player statistics are available for this match.',
  'build-up': 'No goal build-up sequences are available for this match.',
} as const;

const sectionExternalUnavailableMessages = {
  lineup: 'Lineup is not available for this external match.',
  events: 'Events are not available for this external match.',
  'team-stats': 'Team statistics are not available for this external match.',
  'player-stats': 'Player statistics are not available for this external match.',
  'build-up': 'Goal build-up sequences are not available for this external match.',
} as const;

interface SectionState<T> {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  data?: LeagueSectionResponse<T>;
  refetch(): unknown;
}

interface MatchSynchronizationAction {
  error: unknown;
  pending: boolean;
  run(): void;
}

interface ExternalSynchronizationRequest {
  controller: AbortController;
  identity: string;
  matchId: number;
  playerId: string;
  season: number;
  workspaceId: string;
}

const sectionStaleTime = Infinity;

const MatchSection = <T,>({
  title,
  section,
  match,
  query,
  synchronization,
  children,
}: {
  title: string;
  section: keyof typeof sectionEmptyMessages;
  match: LeagueMatchSummary;
  query: SectionState<T>;
  synchronization?: MatchSynchronizationAction;
  children(data: T): React.ReactNode;
}): React.ReactElement => {
  const envelope = query.data;
  const content = envelope?.availability.available && envelope.data ? children(envelope.data) : null;
  const canSynchronize = match.status === 'Played'
    && envelope?.availability.reason === 'not_synchronized'
    && synchronization;
  const unavailable = envelope && !envelope.availability.available
    ? envelope.availability.reason === 'external_unavailable'
      ? <DetailSection state={{ status: 'empty', message: sectionExternalUnavailableMessages[section] }} />
      : canSynchronize
      ? <EmptyState
          title="Analysis is available after this played match is synchronized."
          action={<div className="space-y-3" aria-busy={synchronization.pending}>
            <Button type="button" loading={synchronization.pending} loadingLabel="Synchronizing match" onClick={synchronization.run}>
              {synchronization.error ? 'Retry synchronization' : 'Synchronize this match'}
            </Button>
            {synchronization.pending && <span role="status" aria-label="Synchronizing match analysis." aria-live="polite" className="sr-only">Synchronizing match analysis.</span>}
            {Boolean(synchronization.error) && <p role="alert" className="max-w-md text-sm text-red-700 dark:text-red-300">{getV2Error(synchronization.error).message}</p>}
          </div>}
        />
      : <DetailSection state={{ status: 'empty', message: match.status === 'Played' ? 'Analysis is available after this played match is synchronized.' : 'Analysis is available after this match has been played.' }} />
    : null;
  const empty = envelope?.availability.complete && envelope.availability.stored_count === 0
    ? <DetailSection state={{ status: 'empty', message: sectionEmptyMessages[section] }} />
    : null;
  const cached = unavailable || empty || content;
  if (query.isError) return <SectionQuery title={title} query={query} unframed>{cached}</SectionQuery>;
  if (!query.isPending && unavailable) return unavailable;
  if (!query.isPending && empty) return empty;
  if (!query.isPending && envelope && !content) return <DetailSection state={{ status: 'empty', message: sectionEmptyMessages[section] }} />;
  return <SectionQuery title={title} query={query} unframed>{content}</SectionQuery>;
};

interface LeagueAnalysisProps {
  identityKey: string;
  season: number;
  matchId: number;
  chatSession: string | null;
  open: boolean;
  onOpenChange(open: boolean): void;
  playerOptions: Array<{ playerId: number; name: string; position: string }>;
  send(message: string, preset: LeagueAnalysisPreset | undefined, playerId: number | undefined, sessionId: string | undefined): Promise<{ reply: string; sessionId: string }>;
  listSessions: () => Promise<AIChatSession[]>;
  getSessionMessages: (sessionId: string) => Promise<AIChatMessageRecord[]>;
  deleteSession: (sessionId: string) => Promise<void>;
}

const LeagueAnalysis: React.FC<LeagueAnalysisProps> = ({ identityKey, season, matchId, chatSession, open, onOpenChange, playerOptions, send, listSessions, getSessionMessages, deleteSession }) => {
  const [focusPlayerId, setFocusPlayerId] = useState<number | null>(null);
  return <AIChatPanel
      key={identityKey}
      onSendLatestMessage={(message, preset, sessionId) => send(message, preset as LeagueAnalysisPreset | undefined, focusPlayerId ?? undefined, sessionId)}
      presets={[{ id: 'tactical', label: 'Tactical Analysis' }, { id: 'key_moments', label: 'Key Moments' }, { id: 'team_comparison', label: 'Team Comparison' }, { id: 'defensive_review', label: 'Defensive Review' }]}
      playerOptions={playerOptions}
      onPlayerFocusChange={setFocusPlayerId}
      title="AI Match Analysis"
      entityType="league_match"
      entityId={`${season}:${matchId}`}
      open={open}
      onOpenChange={onOpenChange}
      initialSessionId={chatSession ?? undefined}
      listSessions={listSessions}
      getSessionMessages={getSessionMessages}
      deleteSession={deleteSession}
      maxMessageLength={4000}
    />;
};

const MatchDetailWorkspace: React.FC<{ workspaceId: string }> = ({ workspaceId }) => {
  const { matchId: rawId } = useParams<{ matchId: string }>();
  const id = positiveRouteId(rawId);
  const location = useLocation();
  const navigationType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();
  const client = useQueryClient();
  const state = parseLeagueMatchDetailUrl(searchParams);
  const externalPlayer = state.player ?? undefined;
  const canonicalSearch = serializeLeagueMatchDetailUrl(state).toString();
  const playerReturnTo = state.player
    ? safeExternalPlayerReturnTo((location.state as { from?: unknown } | null)?.from, state.player)
    : null;
  const [goalFocus, setGoalFocus] = useState<GoalFocus | null>(null);
  const [visibleSection, setVisibleSection] = useState<DataSection>(state.tab === 'analysis' ? 'overview' : state.tab);
  const pendingPopRestore = useRef<{ key: string; tab: DataSection; started: boolean } | null>(
    state.tab !== 'overview' && state.tab !== 'analysis' ? { key: location.key, tab: state.tab, started: false } : null,
  );
  const lastLocationKey = useRef(location.key);
  const [, setRestoreComplete] = useState(0);
  if (lastLocationKey.current !== location.key) {
    lastLocationKey.current = location.key;
    if (navigationType === 'POP') {
      pendingPopRestore.current = state.tab === 'analysis' ? null : { key: location.key, tab: state.tab, started: false };
    } else if (navigationType === 'PUSH') {
      pendingPopRestore.current = null;
    }
  }
  const pendingPopKey = pendingPopRestore.current?.key;
  const observeSections = pendingPopRestore.current === null;
  const analysisOpen = useRef(state.tab === 'analysis');
  analysisOpen.current = state.tab === 'analysis';

  useEffect(() => {
    if (!state.invalidPlayer && searchParams.toString() !== canonicalSearch) setSearchParams(canonicalSearch, playerReturnTo
      ? { replace: true, state: { from: playerReturnTo } }
      : { replace: true });
  }, [canonicalSearch, playerReturnTo, searchParams, setSearchParams, state.invalidPlayer]);

  const seasons = useQuery({
    queryKey: leagueQueryKeys.seasons(workspaceId),
    queryFn: ({ signal }) => leagueAPIv2.listSeasons(workspaceId, signal),
    enabled: !state.invalidPlayer,
  });
  const detail = useQuery({
    queryKey: leagueQueryKeys.match(workspaceId, state.season, id || 0, externalPlayer),
    queryFn: ({ signal }) => leagueAPIv2.getMatch(workspaceId, state.season ?? undefined, id as number, externalPlayer, signal),
    enabled: Boolean(id) && !state.invalidPlayer,
    retry: (failureCount, error) => getV2Error(error).code !== 'not_found' && failureCount < 3,
  });
  const resolvedSeason = detail.data?.data.match.season_id ?? state.season;
  const sectionBaseId = `league-match-${resolvedSeason || 'resolve'}-${id || 0}`;
  const sectionEnabled = Boolean(id && resolvedSeason && detail.data) && !state.invalidPlayer;
  const lineup = useQuery({
    queryKey: leagueQueryKeys.matchSection(workspaceId, resolvedSeason || 0, id || 0, 'lineup', externalPlayer),
    queryFn: ({ signal }) => leagueAPIv2.getMatchLineup(workspaceId, resolvedSeason as number, id as number, externalPlayer, signal),
    enabled: sectionEnabled,
    staleTime: sectionStaleTime,
  });
  const events = useQuery({
    queryKey: leagueQueryKeys.matchSection(workspaceId, resolvedSeason || 0, id || 0, 'events', externalPlayer),
    queryFn: ({ signal }) => leagueAPIv2.getMatchEvents(workspaceId, resolvedSeason as number, id as number, externalPlayer, signal),
    enabled: sectionEnabled,
    staleTime: sectionStaleTime,
  });
  const teamStats = useQuery({
    queryKey: leagueQueryKeys.matchSection(workspaceId, resolvedSeason || 0, id || 0, 'team-stats', externalPlayer),
    queryFn: ({ signal }) => leagueAPIv2.getMatchTeamStats(workspaceId, resolvedSeason as number, id as number, externalPlayer, signal),
    enabled: sectionEnabled,
    staleTime: sectionStaleTime,
  });
  const playerStats = useQuery({
    queryKey: leagueQueryKeys.matchSection(workspaceId, resolvedSeason || 0, id || 0, 'player-stats', externalPlayer),
    queryFn: ({ signal }) => leagueAPIv2.getMatchPlayerStats(workspaceId, resolvedSeason as number, id as number, externalPlayer, signal),
    enabled: sectionEnabled,
    staleTime: sectionStaleTime,
  });
  const buildUp = useQuery({
    queryKey: leagueQueryKeys.matchSection(workspaceId, resolvedSeason || 0, id || 0, 'build-up', externalPlayer),
    queryFn: ({ signal }) => leagueAPIv2.getMatchBuildUp(workspaceId, resolvedSeason as number, id as number, externalPlayer, signal),
    enabled: sectionEnabled,
    staleTime: sectionStaleTime,
  });
  const externalSynchronizationController = useRef<AbortController | null>(null);
  const externalSynchronizationInFlight = useRef(false);
  const externalSynchronizationAttempted = useRef<string | null>(null);
  const externalIdentityMounted = useRef(true);
  const [externalSynchronizationError, setExternalSynchronizationError] = useState<unknown>(null);
  const externalIdentity = externalPlayer && id && resolvedSeason
    ? `${workspaceId}:${resolvedSeason}:${id}:${externalPlayer}`
    : null;
  const externalSynchronizationAuthorized = detail.data?.data.full_data_state === 'missing'
    && !detail.isFetching
    && !detail.isError;
  const detailAuthorizationFailed = detail.isError
    && !detail.isFetching
    && ['authentication_required', 'forbidden', 'not_found'].includes(getV2Error(detail.error).code);
  const currentExternalIdentity = useRef(externalIdentity);
  currentExternalIdentity.current = externalIdentity;
  const externalSynchronization = useMutation({
    mutationFn: (request: ExternalSynchronizationRequest) => leagueAPIv2.synchronizeExternalMatch(
      request.workspaceId,
      request.season,
      request.matchId,
      request.playerId,
      request.controller.signal,
    ),
    onSuccess: async (_envelope, request) => {
      if (request.controller.signal.aborted || currentExternalIdentity.current !== request.identity) return;
      await client.invalidateQueries({
        queryKey: leagueQueryKeys.match(request.workspaceId, request.season, request.matchId, request.playerId),
      });
    },
    onError: (error, request) => {
      if (request.controller.signal.aborted || currentExternalIdentity.current !== request.identity) return;
      setExternalSynchronizationError(error);
    },
    onSettled: (_data, _error, request) => {
      if (externalSynchronizationController.current !== request.controller) return;
      externalSynchronizationInFlight.current = false;
      externalSynchronizationController.current = null;
    },
  });
  const startExternalSynchronization = () => {
    if (!externalIdentityMounted.current
      || !externalSynchronizationAuthorized
      || !externalIdentity
      || !externalPlayer
      || !id
      || !resolvedSeason
      || externalSynchronizationInFlight.current) return;
    const controller = new AbortController();
    externalSynchronizationAttempted.current = externalIdentity;
    externalSynchronizationInFlight.current = true;
    externalSynchronizationController.current = controller;
    setExternalSynchronizationError(null);
    externalSynchronization.mutate({
      controller,
      identity: externalIdentity,
      matchId: id,
      playerId: externalPlayer,
      season: resolvedSeason,
      workspaceId,
    });
  };
  const startExternalSynchronizationEffect = useEffectEvent(startExternalSynchronization);
  const externalFetchVisible = Boolean(
    externalIdentity
    && !detailAuthorizationFailed
    && detail.data?.data.full_data_state === 'missing'
    && (externalSynchronizationAuthorized || externalSynchronization.isPending),
  );
  const externalFetchAction = externalFetchVisible ? <div
    className="flex max-w-sm flex-col items-start gap-2 sm:items-end"
    aria-busy={externalSynchronization.isPending}
  >
    <Button
      type="button"
      loading={externalSynchronization.isPending}
      loadingLabel="Fetching match data"
      onClick={startExternalSynchronization}
    >
      {externalSynchronizationError ? 'Retry fetch match data' : 'Fetch match data'}
    </Button>
    {Boolean(externalSynchronizationError) && <p role="alert" className="text-left text-sm text-red-700 dark:text-red-300 sm:text-right">
      Full match data could not be loaded. {getV2Error(externalSynchronizationError).message}
    </p>}
  </div> : undefined;

  useEffect(() => {
    externalIdentityMounted.current = true;
    return () => {
      externalIdentityMounted.current = false;
      externalSynchronizationController.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!externalIdentity
      || !externalSynchronizationAuthorized
      || externalSynchronizationAttempted.current === externalIdentity) return;
    queueMicrotask(startExternalSynchronizationEffect);
    // Effect Events intentionally read the latest mutation state without becoming dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalIdentity, externalSynchronizationAuthorized]);

  useEffect(() => {
    if (detailAuthorizationFailed) externalSynchronizationController.current?.abort();
  }, [detailAuthorizationFailed]);

  useEffect(() => {
    if (detail.data?.data.full_data_state === 'complete') setExternalSynchronizationError(null);
  }, [detail.data?.data.full_data_state]);

  const canAnalyze = hasCapability(detail.data?.capabilities ?? [], 'league.analysis.view');
  const canSynchronize = hasCapability(detail.data?.capabilities ?? [], 'league.sync');
  const analysisSessions = useQuery({
    queryKey: leagueQueryKeys.analysisSessions(workspaceId, resolvedSeason || 0, id || 0),
    queryFn: ({ signal }) => leagueAPIv2.listMatchChatSessions(workspaceId, resolvedSeason as number, id as number, signal),
    enabled: sectionEnabled && canAnalyze,
    staleTime: sectionStaleTime,
  });
  const synchronizationController = useRef<AbortController | null>(null);
  const synchronizationInFlight = useRef(false);
  const identityMounted = useRef(true);
  const synchronization = useMutation({
    mutationFn: () => {
      const controller = new AbortController();
      synchronizationController.current = controller;
      return leagueAPIv2.synchronizeMatch(workspaceId, resolvedSeason as number, id as number, controller.signal);
    },
    onSuccess: async (envelope: LeagueMatchSynchronizationResponse) => {
      if (!identityMounted.current || synchronizationController.current?.signal.aborted) return;
      client.setQueryData<LeagueMatchResponse>(leagueQueryKeys.match(workspaceId, resolvedSeason, id as number), (current: LeagueMatchResponse | undefined) => current ? { ...current, capabilities: envelope.capabilities } : current);
      await client.invalidateQueries({ queryKey: leagueQueryKeys.match(workspaceId, resolvedSeason, id as number) });
    },
    onError: async error => {
      const controller = synchronizationController.current;
      if (getV2Error(error).code !== 'forbidden' || !identityMounted.current || controller?.signal.aborted) return;
      await detail.refetch();
      if (!identityMounted.current || controller?.signal.aborted) return;
    },
    onSettled: () => {
      synchronizationInFlight.current = false;
      synchronizationController.current = null;
    },
  });

  useEffect(() => {
    identityMounted.current = true;
    return () => {
      identityMounted.current = false;
      synchronizationController.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!detail.data || !resolvedSeason || state.season === resolvedSeason) return;
    setSearchParams(serializeLeagueMatchDetailUrl({ ...state, season: resolvedSeason }), playerReturnTo
      ? { replace: true, state: { from: playerReturnTo } }
      : { replace: true });
  }, [detail.data, playerReturnTo, resolvedSeason, setSearchParams, state]);

  const updateCapabilities = useCallback((capabilities: Capability[]) => {
    client.setQueryData<LeagueMatchResponse>(leagueQueryKeys.match(workspaceId, resolvedSeason, id || 0), (current: LeagueMatchResponse | undefined) => current ? { ...current, capabilities } : current);
  }, [client, id, resolvedSeason, workspaceId]);
  const listAnalysisSessions = useCallback(() => client.fetchQuery({
    queryKey: leagueQueryKeys.analysisSessions(workspaceId, resolvedSeason as number, id as number),
    queryFn: ({ signal }) => leagueAPIv2.listMatchChatSessions(workspaceId, resolvedSeason as number, id as number, signal),
    staleTime: sectionStaleTime,
  }).then(envelope => {
    updateCapabilities(envelope.capabilities);
    return envelope.data.sessions;
  }), [client, id, resolvedSeason, updateCapabilities, workspaceId]);
  const getAnalysisSessionMessages = useCallback((sessionId: string) => client.fetchQuery({
    queryKey: leagueQueryKeys.analysisSession(workspaceId, resolvedSeason as number, id as number, sessionId),
    queryFn: ({ signal }) => leagueAPIv2.getMatchChatSession(workspaceId, resolvedSeason as number, id as number, sessionId, signal),
    staleTime: sectionStaleTime,
  }).then(envelope => {
    updateCapabilities(envelope.capabilities);
    return envelope.data.messages;
  }), [client, id, resolvedSeason, updateCapabilities, workspaceId]);
  const deleteAnalysisSession = useCallback(async (sessionId: string) => {
    const envelope = await leagueAPIv2.deleteMatchChatSession(workspaceId, resolvedSeason as number, id as number, sessionId);
    client.removeQueries({ queryKey: leagueQueryKeys.analysisSession(workspaceId, resolvedSeason as number, id as number, sessionId) });
    client.setQueryData<LeagueAnalysisSessionsResponse>(leagueQueryKeys.analysisSessions(workspaceId, resolvedSeason as number, id as number), (current: LeagueAnalysisSessionsResponse | undefined) => current ? { ...current, data: { ...current.data, sessions: current.data.sessions.filter((session: LeagueAnalysisSession) => session.id !== sessionId) }, capabilities: envelope.capabilities } : current);
    updateCapabilities(envelope.capabilities);
  }, [client, id, resolvedSeason, updateCapabilities, workspaceId]);
  const sendAnalysis = useCallback((message: string, preset: LeagueAnalysisPreset | undefined, playerId: number | undefined, sessionId: string | undefined) => leagueAPIv2.sendMatchChat(workspaceId, resolvedSeason as number, id as number, message, preset, playerId, sessionId).then(envelope => {
    updateCapabilities(envelope.capabilities);
    void client.invalidateQueries({ queryKey: leagueQueryKeys.analysisSessions(workspaceId, resolvedSeason as number, id as number) });
    client.removeQueries({ queryKey: leagueQueryKeys.analysisSession(workspaceId, resolvedSeason as number, id as number, envelope.data.sessionId) });
    return envelope.data;
  }), [client, id, resolvedSeason, updateCapabilities, workspaceId]);

  const leagueFallback = resolvedSeason ? `/league?season=${resolvedSeason}` : '/league';
  const playerQualified = Boolean(state.player);
  const back = playerReturnTo ?? safeLeagueReturnTo((location.state as { from?: unknown } | null)?.from, leagueFallback);
  const backLabel = playerQualified ? 'Back to player' : 'Back to league matches';
  const updateUrl = useCallback((tab: LeagueMatchTab, replace: boolean) => {
    setSearchParams(serializeLeagueMatchDetailUrl({ tab, season: resolvedSeason, player: state.player, chatSession: null }), playerReturnTo
      ? { replace, state: { from: playerReturnTo } }
      : { replace });
  }, [playerReturnTo, resolvedSeason, setSearchParams, state.player]);
  const selectSection = useCallback((section: DataSection) => {
    setGoalFocus(null);
    setVisibleSection(section);
    updateUrl(section, false);
  }, [updateUrl]);
  const openAnalysis = useCallback(() => {
    if (analysisOpen.current) return;
    analysisOpen.current = true;
    updateUrl('analysis', false);
  }, [updateUrl]);
  const returnToVisibleSection = useCallback(() => {
    analysisOpen.current = false;
    updateUrl(visibleSection, false);
  }, [updateUrl, visibleSection]);
  const observeSection = useCallback((section: DataSection) => {
    setVisibleSection(section);
    if (!analysisOpen.current) updateUrl(section, true);
  }, [updateUrl]);
  const synchronizeMatch = () => {
    if (synchronizationInFlight.current) return;
    synchronizationInFlight.current = true;
    synchronization.mutate();
  };
  const synchronizationAction = canSynchronize && !externalPlayer ? {
    error: synchronization.error,
    pending: synchronization.isPending,
    run: synchronizeMatch,
  } : undefined;

  useEffect(() => {
    const restore = pendingPopRestore.current;
    if (!detail.data || !resolvedSeason || !restore || restore.started) return undefined;
    restore.started = true;
    setVisibleSection(restore.tab);
    scrollToMatchSection(sectionBaseId, restore.tab);
    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        if (pendingPopRestore.current !== restore) return;
        pendingPopRestore.current = null;
        setRestoreComplete(value => value + 1);
      });
    });
    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame) window.cancelAnimationFrame(secondFrame);
    };
  }, [detail.data, pendingPopKey, resolvedSeason, sectionBaseId]);

  if (state.invalidPlayer) return <DetailPageFrame backHref={back} backLabel={backLabel} identity={<h1 className="text-2xl font-bold">Match details</h1>}><DetailSection state={{ status: 'empty', message: 'This external player link is invalid.' }} /></DetailPageFrame>;
  if (!id) return <DetailPageFrame backHref={back} backLabel={backLabel} identity={<h1 className="text-2xl font-bold">Match details</h1>}><DetailSection state={{ status: 'empty', message: 'This match link is invalid.' }} /></DetailPageFrame>;
  if (detail.isPending && !detail.data) return <DetailSection state={{ status: 'loading', message: 'Resolving match details in this workspace...' }} />;
  if (detail.isError && !detail.data) return <DetailPageFrame backHref={back} backLabel={backLabel} identity={<h1 className="text-2xl font-bold">Match details</h1>}><DetailSection state={{ status: 'error', message: getV2Error(detail.error).message, retry: () => { void detail.refetch(); } }} /></DetailPageFrame>;
  if (!detail.data || !resolvedSeason) return null;

  const match = detail.data.data.match;
  const homeTeamId = match.home_team_id ?? 0;
  const awayTeamId = match.away_team_id ?? 0;
  const homeTeamName = match.home_team_name || 'Home team';
  const awayTeamName = match.away_team_name || 'Away team';
  const playerOptions: Array<{ playerId: number; name: string; position: string }> = analysisSessions.data?.data.players ?? [];
  const playerNames = new Map<number, string>(playerOptions.map((player: { playerId: number; name: string }) => [player.playerId, player.name]));
  for (const player of playerStats.data?.data ?? []) {
    const name = player.name?.trim();
    if (name) playerNames.set(player.playerId, name);
  }
  const viewGoalBuildUp = (goal: GoalFocus) => {
    setGoalFocus(goal);
    setVisibleSection('build-up');
    const nextSearch = serializeLeagueMatchDetailUrl({ tab: 'build-up', season: resolvedSeason, player: state.player, chatSession: null });
    if (playerReturnTo) setSearchParams(nextSearch, { state: { from: playerReturnTo } });
    else setSearchParams(nextSearch);
    scrollToMatchSection(sectionBaseId, 'build-up');
  };

  return <DetailPageFrame
    backHref={back}
    backLabel={backLabel}
    identity={<><p className="text-sm font-semibold uppercase tracking-wide text-gray-500">Match details</p><h1 className="break-words text-2xl font-bold text-gray-950 dark:text-white">{matchName(match)}</h1><p>{matchDate(match.date_utc)}{match.gameweek !== null ? ` · Gameweek ${match.gameweek}` : ''}</p></>}
    status={<div className="flex flex-wrap items-center gap-3"><StatusLabel value={match.status} /><strong className="text-xl">{matchScore(match)}</strong><span>{state.player ? 'External scouting match' : match.stats_synced ? 'Analysis synchronized' : 'Analysis not synchronized'}</span></div>}
    actions={externalFetchAction}
    tabs={<MatchSectionNavigation baseId={sectionBaseId} sections={dataSections} analysis={analysisItem} showAnalysis={canAnalyze} active={visibleSection} analysisOpen={state.tab === 'analysis'} observe={observeSections} onSelect={selectSection} onAnalysisSelect={openAnalysis} onVisibleSectionChange={observeSection} />}
    stickyTabs
  >
    {seasons.isError && seasons.data && <p role="alert" className="rounded-lg bg-amber-50 p-3 text-amber-900 dark:bg-amber-950 dark:text-amber-100">Available seasons could not refresh: {getV2Error(seasons.error).message}. Cached season context remains visible. <button className="font-semibold underline" onClick={() => { void seasons.refetch(); }}>Retry seasons</button></p>}
    {detail.isError && <p role="alert" className="rounded-lg bg-amber-50 p-3 text-amber-900 dark:bg-amber-950 dark:text-amber-100">Details could not refresh. Existing data remains visible. <button className="font-semibold underline" onClick={() => { void detail.refetch(); }}>Retry</button></p>}
    {state.tab === 'analysis' && !canAnalyze && <DetailSection title="Analysis unavailable"><p>You do not have permission to view league match analysis.</p></DetailSection>}
    <MatchPageSection baseId={sectionBaseId} id="overview" label="Overview">
      <WyscoutMatchOverview match={{ kickoffAt: match.date_utc || '', status: match.status, label: match.label || undefined, homeTeam: { id: homeTeamId, name: homeTeamName }, awayTeam: { id: awayTeamId, name: awayTeamName }, score: { home: match.home_score, away: match.away_score } }} stats={detail.data.data.overview.team_stats} events={detail.data.data.overview.events} />
    </MatchPageSection>
    <MatchPageSection baseId={sectionBaseId} id="lineup" label="Lineup">
      <MatchSection<LeagueLineupSection> title="Lineup" section="lineup" match={match} query={lineup} synchronization={synchronizationAction}>{data => <WyscoutLineups lineup={data.lineup} homeTeamName={homeTeamName} awayTeamName={awayTeamName} kickoffAt={match.date_utc || ''} playerNames={playerNames} />}</MatchSection>
    </MatchPageSection>
    <MatchPageSection baseId={sectionBaseId} id="events" label="Events">
      <MatchSection<LeagueEventsSection> title="Events" section="events" match={match} query={events} synchronization={synchronizationAction}>{data => <WyscoutEventsAnalysis events={data.events} matchId={id} canWatchVideo={false} requestEventVideo={eventId => matchesV2API.wyscoutEventVideo(workspaceId, id, eventId)} homeTeamId={homeTeamId} awayTeamId={awayTeamId} homeTeamName={homeTeamName} awayTeamName={awayTeamName} onViewGoalBuildUp={viewGoalBuildUp} />}</MatchSection>
    </MatchPageSection>
    <MatchPageSection baseId={sectionBaseId} id="team-stats" label="Team stats">
      <MatchSection<LeagueTeamStatsSection> title="Team stats" section="team-stats" match={match} query={teamStats} synchronization={synchronizationAction}>{data => <MatchAdvancedStats matchId={id} homeTeamId={homeTeamId} awayTeamId={awayTeamId} homeTeamName={homeTeamName} awayTeamName={awayTeamName} statsData={data} />}</MatchSection>
    </MatchPageSection>
    <MatchPageSection baseId={sectionBaseId} id="player-stats" label="Player stats">
      <MatchSection<LeaguePlayerStatsSection> title="Player stats" section="player-stats" match={match} query={playerStats} synchronization={synchronizationAction}>{data => <MatchPlayerStats matchId={id} homeTeamId={homeTeamId} awayTeamId={awayTeamId} homeTeamName={homeTeamName} awayTeamName={awayTeamName} playersData={data} />}</MatchSection>
    </MatchPageSection>
    <MatchPageSection baseId={sectionBaseId} id="build-up" label="Build-up" last>
      <MatchSection<LeagueBuildUpSection> title="Build-up" section="build-up" match={match} query={buildUp} synchronization={synchronizationAction}>{data => <MatchBuildUpPlays matchId={id} homeTeamId={homeTeamId} awayTeamId={awayTeamId} homeTeamName={homeTeamName} awayTeamName={awayTeamName} goalGroups={data.goalGroups} focusedGoalMinute={goalFocus?.minute} focusedGoalTeamId={goalFocus?.teamId} />}</MatchSection>
    </MatchPageSection>
    {canAnalyze && <LeagueAnalysis
        key={`${workspaceId}:${resolvedSeason}:${id}:${state.chatSession ?? 'new'}`}
        identityKey={`${workspaceId}:${resolvedSeason}:${id}:${state.chatSession ?? 'new'}`}
        season={resolvedSeason}
        matchId={id}
        chatSession={state.chatSession}
        open={state.tab === 'analysis'}
        onOpenChange={open => open ? openAnalysis() : returnToVisibleSection()}
        playerOptions={playerOptions}
        send={sendAnalysis}
        listSessions={listAnalysisSessions}
        getSessionMessages={getAnalysisSessionMessages}
        deleteSession={deleteAnalysisSession}
      />}
  </DetailPageFrame>;
};

const LeagueMatchDetailPage: React.FC = () => {
  const workspaceId = useLeagueRuntime().workspaceId;
  const rawId = useParams<{ matchId: string }>().matchId || '';
  const [searchParams] = useSearchParams();
  const state = parseLeagueMatchDetailUrl(searchParams);
  return workspaceId ? <MatchDetailWorkspace key={`${workspaceId}:${state.season ?? 'resolve'}:${rawId}:${state.invalidPlayer ? 'invalid-player' : state.player ?? 'league'}`} workspaceId={workspaceId} /> : <DelayedLoading loading><LoadingStatus variant="page" label="Preparing league match workspace" /></DelayedLoading>;
};

export default LeagueMatchDetailPage;
